import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText,
  ChartBar,
  CurrencyEur,
  House,
  Clock,
  ArrowLeft,
  Globe,
  CheckCircle,
  HourglassSimple
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const languageFlags = { ITA: 'IT', ENG: 'EN', FRA: 'FR', SPA: 'ES', TED: 'DE' };

export default function StatisticsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  const maxMonthCount = stats?.by_month?.length ? Math.max(...stats.by_month.map(m => m.count)) : 1;

  const monthNames = {
    '01': 'Gen', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'Mag', '06': 'Giu',
    '07': 'Lug', '08': 'Ago', '09': 'Set', '10': 'Ott', '11': 'Nov', '12': 'Dic'
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="statistics-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-zinc-100 transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={22} weight="bold" className="text-zinc-700" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">Statistiche</h1>
              <p className="text-xs text-zinc-400 hidden sm:block">Panoramica della tua attività</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ChartBar size={24} weight="bold" className="text-zinc-400" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-total-contracts">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={20} className="text-zinc-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Contratti</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-zinc-900">{stats?.total_contracts || 0}</p>
            <p className="text-xs text-zinc-400 mt-1">totali creati</p>
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-signed-contracts">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Firmati</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-green-700">{stats?.signed_contracts || 0}</p>
            <p className="text-xs text-zinc-400 mt-1">contratti completati</p>
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-revenue">
            <div className="flex items-center gap-2 mb-3">
              <CurrencyEur size={20} className="text-zinc-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Fatturato</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-zinc-900">&euro;{stats?.total_revenue?.toLocaleString('it-IT') || '0'}</p>
            <p className="text-xs text-zinc-400 mt-1">da contratti firmati</p>
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-avg-nights">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={20} className="text-zinc-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Media notti</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-zinc-900">{stats?.avg_nights || 0}</p>
            <p className="text-xs text-zinc-400 mt-1">notti per soggiorno</p>
          </div>
        </div>

        {/* Receipts KPI */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8 mt-4" data-testid="receipts-stats-section">
          <div className="bg-white border border-teal-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={20} className="text-teal-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-teal-600">Ricevute</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-teal-700">{stats?.total_receipts || 0}</p>
            <p className="text-xs text-zinc-400 mt-1">ricevute create</p>
          </div>

          <div className="bg-white border border-teal-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <CurrencyEur size={20} className="text-teal-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-teal-600">Totale Ricevute</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-teal-700">&euro;{stats?.total_receipt_revenue?.toLocaleString('it-IT') || '0'}</p>
            <p className="text-xs text-zinc-400 mt-1">incassato da ricevute</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Monthly Chart */}
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-monthly-chart">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4">Contratti per mese</h3>
            {stats?.by_month?.length > 0 ? (
              <div className="space-y-2">
                {stats.by_month.map((item) => {
                  const [year, month] = item.month.split('-');
                  const label = `${monthNames[month]} ${year.slice(2)}`;
                  const width = Math.max((item.count / maxMonthCount) * 100, 8);
                  return (
                    <div key={item.month} className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500 w-14 text-right font-medium">{label}</span>
                      <div className="flex-1 h-7 bg-zinc-100 relative">
                        <div
                          className="h-full bg-zinc-900 transition-all duration-500"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-zinc-700 w-8">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 py-8 text-center">Nessun dato disponibile</p>
            )}
          </div>

          {/* By Language */}
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-by-language">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4">Contratti per lingua</h3>
            {stats?.by_language && Object.keys(stats.by_language).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.by_language)
                  .sort(([, a], [, b]) => b - a)
                  .map(([lang, count]) => {
                    const total = Object.values(stats.by_language).reduce((a, b) => a + b, 0);
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={lang} className="flex items-center gap-3">
                        <div className="flex items-center gap-2 w-16">
                          <Globe size={16} className="text-zinc-400" />
                          <span className="text-sm font-bold text-zinc-700">{languageFlags[lang] || lang}</span>
                        </div>
                        <div className="flex-1 h-7 bg-zinc-100 relative">
                          <div
                            className="h-full bg-zinc-700 transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-zinc-700 w-16 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 py-8 text-center">Nessun dato disponibile</p>
            )}
          </div>

          {/* By Property */}
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-by-property">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4">Contratti per alloggio</h3>
            {stats?.by_property?.length > 0 ? (
              <div className="space-y-3">
                {stats.by_property.map((item) => (
                  <div key={item.name} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <House size={16} className="text-zinc-400" />
                      <span className="text-sm text-zinc-700">{item.name}</span>
                    </div>
                    <span className="text-sm font-bold text-zinc-900">{item.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 py-8 text-center">Nessun dato disponibile</p>
            )}
          </div>

          {/* Summary Card */}
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="stat-summary">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4">Riepilogo</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <House size={16} className="text-zinc-400" />
                  <span className="text-sm text-zinc-600">Alloggi registrati</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">{stats?.properties_count || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <HourglassSimple size={16} className="text-amber-500" />
                  <span className="text-sm text-zinc-600">In attesa di firma</span>
                </div>
                <span className="text-sm font-bold text-amber-600">{stats?.pending_contracts || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span className="text-sm text-zinc-600">Tasso di completamento</span>
                </div>
                <span className="text-sm font-bold text-green-700">
                  {stats?.total_contracts > 0
                    ? Math.round((stats.signed_contracts / stats.total_contracts) * 100)
                    : 0}%
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-zinc-400" />
                  <span className="text-sm text-zinc-600">Log storici (GDPR)</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">{stats?.logs_count || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
